import React, { useEffect, useRef, useState } from 'react';
import { loadPayPalSdk, createTipOrder, captureTipOrder } from '../services/tipPaymentService.js';

/**
 * Tip the DJ — USD 1.00 PayPal checkout.
 *
 * Deliberately thin: it renders PayPal's own buttons and reports the outcome.
 * The price and the DJ being credited are both decided server-side, so there is
 * nothing here worth tampering with.
 *
 * onPaid() fires ONLY on a completed capture. Everything celebratory —
 * the burst, the toast, the counter — hangs off that, so a guest who opens
 * PayPal and backs out never sees a thank-you for money that never moved.
 */
const TipPayModal = ({ venueUid, fromUid, onPaid, onClose }) => {
  const boxRef = useRef(null);
  const [status, setStatus] = useState('loading');   // loading | ready | paying | error
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let buttons = null;

    (async () => {
      try {
        const paypal = await loadPayPalSdk();
        if (cancelled || !boxRef.current) return;

        buttons = paypal.Buttons({
          style: { layout: 'vertical', shape: 'pill', label: 'pay', height: 44 },
          createOrder: () => createTipOrder(venueUid),
          onApprove: async (data) => {
            setStatus('paying');
            try {
              const ok = await captureTipOrder(data.orderID, venueUid, fromUid);
              if (cancelled) return;
              if (ok) { onPaid(); onClose(); }
              else { setStatus('error'); setError('The payment did not complete.'); }
            } catch (e) {
              if (!cancelled) { setStatus('error'); setError(e.message || 'The payment could not be completed.'); }
            }
          },
          // A guest closing PayPal is normal, not a failure — say nothing.
          onCancel: () => { if (!cancelled) setStatus('ready'); },
          onError: (e) => {
            console.error('[TipPayModal] PayPal error:', e);
            if (!cancelled) { setStatus('error'); setError('PayPal could not be reached. Please try again.'); }
          },
        });

        await buttons.render(boxRef.current);
        if (!cancelled) setStatus('ready');
      } catch (e) {
        if (!cancelled) { setStatus('error'); setError(e.message || 'Could not load PayPal.'); }
      }
    })();

    return () => {
      cancelled = true;
      // Tear the iframe down, or a remount stacks a second set of buttons.
      try { buttons?.close(); } catch (_) {}
    };
  }, [venueUid, fromUid, onPaid, onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100001, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
        background: 'rgba(0,0,0,.75)', backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 380, borderRadius: 20, padding: 22,
          background: 'linear-gradient(180deg,#151C2E 0%,#111827 100%)',
          border: '1px solid rgba(255,255,255,.15)', color: '#E8EAF0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Tip the DJ</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 0, color: '#8892A4', fontSize: 26, lineHeight: 1, cursor: 'pointer' }}
          >
            ×
          </button>
        </div>

        <p style={{ margin: '0 0 16px', fontSize: 16, color: '#8892A4' }}>
          Show the DJ some love — <strong style={{ color: '#E8EAF0' }}>$1</strong> goes straight to tonight&apos;s set.
        </p>

        {status === 'loading' && (
          <div style={{ fontSize: 16, color: '#8892A4', padding: '12px 0' }}>Loading PayPal…</div>
        )}
        {status === 'paying' && (
          <div style={{ fontSize: 16, color: '#F4B942', padding: '12px 0' }}>Taking payment — one moment…</div>
        )}
        {status === 'error' && (
          <div style={{ fontSize: 16, color: '#FF8FA3', padding: '8px 0 12px' }}>{error}</div>
        )}

        {/* Kept mounted in every state: PayPal renders into this node, and
            unmounting it mid-flow would kill the checkout the guest is in. */}
        <div ref={boxRef} style={{ display: status === 'paying' ? 'none' : 'block' }} />
      </div>
    </div>
  );
};

export default TipPayModal;
